import React, { useEffect, useState } from 'react';
import { ChevronLeft, UserCircle, Crown, Heart, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase.ts';
import { UIImage, LeaderboardEntry, AppView } from '../types.ts';
import { getAuraData } from '../constants.tsx';

interface ProfileViewProps {
  userId: string;
  setActiveView: (view: AppView) => void;
  notify: (msg: string, type?: any) => void;
  setPreviewImage: (img: UIImage | null) => void;
  currentUserId?: string;
  isGuest: boolean;
  addAura: (amount: number) => Promise<number>;
}

export default function ProfileView({ 
  userId, setActiveView, notify, setPreviewImage, currentUserId, isGuest, addAura 
}: ProfileViewProps) {
  const [profile, setProfile] = useState<LeaderboardEntry | null>(null);
  const [showcase, setShowcase] = useState<UIImage[]>([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setIsLoading(true);
      try {
        const { data: profileData } = await supabase.from('profiles').select('*').eq('id', userId).single();
        if (profileData) {
          setProfile(profileData as LeaderboardEntry);
          const { data: memes } = await supabase.from('memes').select('*').eq('user_id', userId).eq('is_public', true).order('timestamp', { ascending: false });
          setShowcase(memes || []);
          
          if (currentUserId && !isGuest) {
            const { data: follow } = await supabase.from('follows').select('*').eq('follower_id', currentUserId).eq('following_id', userId).maybeSingle();
            setIsFollowing(!!follow);
          }
        }
      } catch (e) {
        notify("PROFILE ERROR", "error");
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, [userId, currentUserId, isGuest, notify]);

  const handleFollow = async () => {
    if (isGuest) {
      notify("LOGIN TO FOLLOW", "info");
      return;
    }
    try {
      if (isFollowing) {
        await supabase.from('follows').delete().eq('follower_id', currentUserId).eq('following_id', userId);
        setIsFollowing(false);
        // 🛡️ ANTI-SPAM: Revoke Aura on unfollow
        await addAura(-5);
        notify("UNFOLLOWED SIGMA", "info");
      } else {
        await supabase.from('follows').insert({ follower_id: currentUserId, following_id: userId });
        setIsFollowing(true);
        // 🗿 Gained Aura synced with guide (+5)
        await addAura(5);
        notify("SIGMA FOLLOWED", "success");
      }
    } catch (e) {
      notify("ACTION FAILED", "error");
    }
  };

  const getRarityStyles = (rarity?: string) => {
    switch (rarity) {
      case 'rare': return 'border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.4)]';
      case 'legendary': return 'border-yellow-400 shadow-[0_0_20px_rgba(250,196,21,0.5)]';
      case 'cursed': return 'border-red-600 shadow-[0_0_20px_rgba(220,38,38,0.6)]';
      case 'god': return 'animate-rainbow-border border-white shadow-[0_0_30px_rgba(255,255,255,0.7)]';
      default: return 'border-zinc-800';
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto p-20 flex flex-col items-center gap-6 opacity-40">
        <Loader2 size={64} className="animate-spin text-yellow-400" />
        <span className="font-bungee text-2xl uppercase text-zinc-400">Loading Sigma Profile...</span>
      </div>
    );
  }

  const aura = profile ? getAuraData(profile.xp) : null;

  return (
    <div className="max-w-5xl mx-auto space-y-12 animate-in slide-in-from-bottom-8">
      <div className="flex items-center gap-4">
        <button onClick={() => setActiveView('leaderboard')} className="p-3 bg-zinc-900 rounded-2xl hover:scale-110 active:scale-90 transition-transform shadow-lg">
          <ChevronLeft size={24} />
        </button>
        <h2 className="text-5xl font-bungee text-yellow-400 uppercase tracking-tighter">SIGMA SCAN</h2>
      </div>

      {profile && aura ? (
        <div className="space-y-12">
          {/* Hero Section - Updated background to be less transparent (bg-zinc-900/90) */}
          <div className="bg-zinc-900/90 border-4 border-zinc-800 rounded-[3rem] p-10 flex flex-col md:flex-row items-center gap-10 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 text-zinc-800 pointer-events-none opacity-20"><Crown size={120} /></div>
            <div className="w-48 h-48 rounded-[3rem] overflow-hidden border-4 border-yellow-400 shadow-xl shrink-0">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} className="w-full h-full object-cover" alt={profile.username} />
              ) : (
                <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                  <UserCircle size={64} className="text-zinc-700"/>
                </div>
              )}
            </div>
            <div className="flex-1 space-y-4 text-center md:text-left z-10">
              <div className="space-y-1">
                <h3 className="text-5xl font-bungee uppercase tracking-tight">{profile.username}</h3>
                <p className="text-yellow-400 font-bungee text-xl">{aura.title}</p>
              </div>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4">
                <div className="bg-zinc-800 px-4 py-2 rounded-xl border border-zinc-700 font-bungee text-sm text-zinc-400">LVL {aura.level}</div>
                <div className="bg-zinc-800 px-4 py-2 rounded-xl border border-zinc-700 font-bungee text-sm text-yellow-400">{profile.xp.toLocaleString()} AURA</div>
              </div>
              {userId !== currentUserId && (
                <button 
                  onClick={handleFollow} 
                  className={`px-8 py-4 rounded-2xl font-bungee uppercase shadow-lg transition-all ${isFollowing ? 'bg-zinc-800 text-zinc-500' : 'bg-yellow-400 text-black hover:scale-105 active:translate-y-1'}`}
                  aria-label={isFollowing ? `Unfollow ${profile.username}` : `Follow ${profile.username}`}
                >
                  {isFollowing ? 'Following Sigma' : 'Follow Sigma'}
                </button>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="font-bungee text-xl text-zinc-500 px-4 uppercase tracking-widest border-l-4 border-yellow-400">PUBLIC SHOWCASE</h4>
            {showcase.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
                {showcase.map(meme => (
                  <div 
                    key={meme.id} 
                    onClick={() => setPreviewImage(meme)} 
                    className={`aspect-square rounded-[2rem] overflow-hidden border-4 shadow-xl transition-all hover:scale-105 cursor-pointer ${getRarityStyles(meme.rarity)}`}
                  >
                    <img src={meme.url} className="w-full h-full object-cover" alt={meme.name} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-20 text-center font-bungee opacity-20 text-2xl uppercase tracking-widest bg-zinc-900/20 rounded-[2.5rem] border-4 border-dashed border-zinc-800">
                No Public Rots Found
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="p-20 text-center font-bungee text-zinc-600 uppercase text-3xl">Profile Lost in Ohio</div>
      )}
    </div>
  );
}
